import { concat, fn } from '@ember/helper';
import { get } from '@ember/object';
import Component from '@glimmer/component';
import { type DetailedChangeset } from 'ember-changeset/types';
import PowerSelect from 'ember-power-select/components/power-select';
import { or } from 'ember-truth-helpers';
import isRemovedString from 'garaje/helpers/is-removed-string';
import removedString from 'garaje/helpers/removed-string';
import type EmployeeModel from 'garaje/models/employee';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import { firstLowercase } from 'garaje/utils/first-lowercase';
import { empty, not, and } from 'macro-decorators';
import { cached } from 'tracked-toolbox';

interface EntriesHostFieldSignature {
  Args: {
    allowClear?: boolean;
    changeset: DetailedChangeset<SignInFieldModel> | SignInFieldModel;
    disabled?: boolean;
    errors?: string[] | string;
    label: string;
    required?: boolean;
    selected: Partial<EmployeeModel> | null;
    search: (term: string) => Promise<EmployeeModel[]>;
    update: (model: DetailedChangeset<SignInFieldModel> | SignInFieldModel, selected: EmployeeModel) => unknown;
    validated?: boolean;
  };
  Element: HTMLDivElement;
}

export default class EntriesHostField extends Component<EntriesHostFieldSignature> {
  @empty('args.errors') declare hasNoErrors: boolean;
  @not('hasNoErrors') declare hasErrors: boolean;
  @and('args.validated', 'hasErrors') declare isDisplayingErrors: boolean;

  @cached
  get selected(): Partial<EmployeeModel> | null {
    const { selected } = this.args;
    // eslint-disable-next-line ember/no-get
    if (isRemovedString(get(selected ?? {}, 'name'))) {
      // eslint-disable-next-line ember/no-get
      return { name: removedString(get(selected ?? {}, 'name')) };
    }
    return selected;
  }

  <template>
    <div data-test-flow-field={{@changeset.name}} data-test-custom-field={{@changeset.name}} ...attributes>
      <label
        data-test-select-label
        for={{@changeset.id}}
        class="{{if this.isDisplayingErrors 'error-description'}} {{if @disabled 'cursor-default'}}"
      >
        {{@label}}
        {{#if @required}}
          <span class="requiredField"></span>
        {{/if}}
      </label>
      <div style="margin-bottom:20px;" class="w-full" data-test-option-select>
        <PowerSelect
          @allowClear={{@allowClear}}
          @placeholder={{concat "Choose " (firstLowercase @label)}}
          @selected={{this.selected}}
          @disabled={{or @disabled (isRemovedString @selected.name)}}
          @triggerClass={{if this.isDisplayingErrors "power-select-error"}}
          @search={{@search}}
          @searchEnabled={{true}}
          @onChange={{fn @update @changeset}}
          as |employee|
        >
          {{employee.name}}
        </PowerSelect>
      </div>
      {{#if this.isDisplayingErrors}}
        <div data-test-input-error class="bottom-error">This is a required field</div>
      {{/if}}
    </div>
  </template>
}
